import * as Sentry from "@sentry/vue";
import { isValidUuidString, normalizeUuidRouteParam } from "@/util/uuid";
import { learningServiceRequest, skip } from "./utils";

/**
 * Normalizes and validates a UUID route param. On failure: reports a Sentry
 * warning tagged with the raw and normalized values, then throws a descriptive
 * Error. Callers receive the normalized id to safely interpolate into URLs.
 */
const assertValidUuid = (methodName, paramName, value) => {
  const normalized = normalizeUuidRouteParam(value);
  if (normalized === undefined || !isValidUuidString(normalized, { trim: false })) {
    Sentry.captureMessage(`[programSessions] ${methodName}: missing or invalid ${paramName}`, {
      level: "warning",
      tags: {
        [paramName]: value,
        [`normalized_${paramName}`]: normalized ?? "",
      },
    });
    throw new Error(`${methodName}: ${paramName} must be a valid UUID`);
  }
  return normalized;
};

const programSessionsService = {
  async getProgramSessionsV3({ limit = 10, page = 1, q = "", source, extraParams = {} }) {
    return await learningServiceRequest({
      method: "get",
      url: "/v3/programming/sessions",
      source,
      params: {
        limit,
        skip: skip(page, limit),
        q,
        ...extraParams,
      },
      failureMessage: "Error loading program sessions data",
    });
  },
  async getProgramSessions({ limit = 10, page = 1, q = "", source, extraParams = {} }) {
    return await learningServiceRequest({
      method: "get",
      url: "/programming/sessions",
      source,
      params: {
        limit,
        skip: skip(page, limit),
        q,
        ...extraParams,
      },
      failureMessage: "Error loading program sessions data",
    });
  },
  async getProgramSession({ id, skipCache = false, ...params }) {
    return await learningServiceRequest({
      method: "get",
      url: `/programming/sessions/${id}`,
      failureMessage: "Error loading program session data",
      params,
      headers: skipCache ? { "cache-control": "no-cache" } : {},
    });
  },
  async updateOrCreateProgramSession({ updatedSession }) {
    if (updatedSession.id) {
      return await learningServiceRequest({
        method: "put",
        url: `/programming/sessions/${updatedSession.id}?rules=True&housing=True`,
        payload: updatedSession,
        skipErrorBus: true,
      });
    } else {
      return await learningServiceRequest({
        method: "post",
        url: `/programming/sessions/?rules=True&housing=True`,
        payload: updatedSession,
        skipErrorBus: true,
      });
    }
  },
  async archiveProgramSession(programSessionId) {
    return await learningServiceRequest({
      method: "put",
      url: `/programming/sessions/archive/${programSessionId}`,
    });
  },
  async archiveManyProgramSession(programSessionIds) {
    return await learningServiceRequest({
      method: "put",
      url: `/programming/sessions/archive/`,
      payload: programSessionIds,
    });
  },
  async duplicateProgramSession({ id }) {
    return await learningServiceRequest({
      method: "post",
      url: `/programming/sessions/${id}/copy`,
      failureMessage: "Error loading program session data",
    });
  },
  async getProgramSessionSummary({ id, ...params }) {
    return await learningServiceRequest({
      method: "get",
      url: `/programming/sessions/${id}/summary`,
      failureMessage: "Error loading program session data",
      params,
    });
  },
  async getProgramSessionTodos({ session_id, limit = 10, page = 1, ...extraParams }) {
    const normalizedSessionId = assertValidUuid("getProgramSessionTodos", "session_id", session_id);
    return await learningServiceRequest({
      method: "get",
      url: `/programming/sessions/${normalizedSessionId}/todos`,
      failureMessage: "Error loading program session data",
      params: {
        limit,
        skip: skip(page, limit),
        ...extraParams,
      },
    });
  },
  async getSessionTodoAssignments({ session_id, todo_ids }) {
    const normalizedSessionId = assertValidUuid("getSessionTodoAssignments", "session_id", session_id);
    return await learningServiceRequest({
      method: "get",
      url: `/programming/sessions/${normalizedSessionId}/todos/assigned/`,
      failureMessage: "Error loading session todo assignments",
      params: { todo_ids },
    });
  },
  async createSessionTodo({ session_id, payload }) {
    const normalizedSessionId = assertValidUuid("createSessionTodo", "session_id", session_id);
    return await learningServiceRequest({
      method: "post",
      url: `/programming/sessions/${normalizedSessionId}/todos/`,
      payload,
      skipErrorBus: true,
    });
  },
  async updateSessionTodo({ session_todo_id, payload }) {
    const normalizedSessionTodoId = assertValidUuid("updateSessionTodo", "session_todo_id", session_todo_id);
    return await learningServiceRequest({
      method: "put",
      url: `/programming/sessions/todos/${normalizedSessionTodoId}/`,
      payload,
      skipErrorBus: true,
    });
  },
  async archiveSessionTodo({ session_todo_id }) {
    const normalizedSessionTodoId = assertValidUuid("archiveSessionTodo", "session_todo_id", session_todo_id);
    return await learningServiceRequest({
      method: "put",
      url: `/programming/sessions/todos/archive/${normalizedSessionTodoId}/`,
      skipErrorBus: true,
    });
  },
};

export default programSessionsService;
